import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import { 
    ChevronLeft, Home, Plus, Trash2, Settings2, Info, 
    ListPlus, Calculator, LayoutGrid, HelpCircle, GripVertical 
} from 'lucide-react';
import '../styles/Dashboard.css';

export default function Adicionais() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [grupos, setGrupos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editandoId, setEditandoId] = useState(null);
    const [showGuia, setShowGuia] = useState(false);

    const initialState = {
        nome: '', 
        tipo: 'obrigatorio', 
        minimo: 1, 
        maximo: 1,
        metodo_preco: 'soma', 
        itens: [] 
    };
    const [novoGrupo, setNovoGrupo] = useState(initialState);

    const carregarDados = async () => {
        try {
            const { data } = await api.get(`/cardapio/adicionais/${id}`);
            const formatados = data.map(g => ({
                ...g,
                itens: typeof g.itens_json === 'string' ? JSON.parse(g.itens_json) : (g.itens_json || [])
            }));
            // Ordena os grupos pela propriedade 'ordem' vinda do banco
            setGrupos(formatados.sort((a, b) => a.ordem - b.ordem));
        } catch (e) { toast.error("Erro ao carregar dados."); }
        finally { setLoading(false); }
    };

    useEffect(() => { carregarDados(); }, [id]);

    // Lógica para Reordenar e Salvar no Banco
    const onDragEnd = async (result) => {
        const { destination, source } = result;

        if (!destination || (destination.droppableId === source.droppableId && destination.index === source.index)) {
            return;
        }

        const novaLista = Array.from(grupos);
        const [reorderedItem] = novaLista.splice(source.index, 1);
        
        // Se mudar de coluna (tipo), atualizamos o tipo do item
        if (source.droppableId !== destination.droppableId) {
            reorderedItem.tipo = destination.droppableId;
        }

        novaLista.splice(destination.index, 0, reorderedItem);
        setGrupos(novaLista);

        try {
            const ordens = novaLista.map((g, index) => ({ id: g.id, ordem: index, tipo: g.tipo }));
            await api.post(`/cardapio/adicionais/reordenar`, { ordens });
            toast.success("Ordem atualizada!");
        } catch (e) {
            toast.error("Erro ao salvar nova ordem.");
            carregarDados();
        }
    };

    const handleSalvar = async (e) => {
        e.preventDefault();
        if (novoGrupo.itens.length === 0) {
            return toast.error("Adicione pelo menos um item ao grupo!");
        }

        try {
            const payload = { 
                ...novoGrupo, 
                minimo: parseInt(novoGrupo.minimo) || 0,
                maximo: parseInt(novoGrupo.maximo) || 1,
                estabelecimento_id: id,
                itens_json: JSON.stringify(novoGrupo.itens) 
            };

            if (editandoId) await api.put(`/cardapio/adicionais/${editandoId}`, payload);
            else await api.post('/cardapio/adicionais', payload);
            
            fecharModal();
            carregarDados();
            toast.success("Regras aplicadas com sucesso!");
        } catch (e) { toast.error("Erro ao salvar grupo."); }
    };

    const adicionarItemAoGrupo = () => {
        setNovoGrupo({ ...novoGrupo, itens: [...novoGrupo.itens, { nome: '', preco: '0' }] });
    };

    const atualizarItem = (index, campo, valor) => {
        const novosItens = [...novoGrupo.itens];
        novosItens[index][campo] = valor;
        setNovoGrupo({ ...novoGrupo, itens: novosItens });
    };

    const removerItem = (index) => {
        const novosItens = novoGrupo.itens.filter((_, i) => i !== index);
        setNovoGrupo({ ...novoGrupo, itens: novosItens });
    };

    const abrirEdicao = (grupo) => {
        setEditandoId(grupo.id);
        setNovoGrupo({ 
            nome: grupo.nome,
            tipo: grupo.tipo,
            minimo: grupo.minimo,
            maximo: grupo.maximo,
            metodo_preco: grupo.metodo_preco || 'soma',
            itens: grupo.itens || [] 
        });
        setShowModal(true);
    };

    const fecharModal = () => {
        setShowModal(false);
        setEditandoId(null);
        setNovoGrupo(initialState);
        setShowGuia(false);
    };

    const renderColumn = (titulo, tipoId, icone, cor) => (
        <Droppable droppableId={tipoId}>
            {(provided) => (
                <div className="flow-column" {...provided.droppableProps} ref={provided.innerRef}>
                    <div className="column-header" style={{ borderBottom: `3px solid ${cor}` }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            {icone} <h3>{titulo}</h3>
                        </div>
                        <button className="btn-add-column" onClick={() => { setNovoGrupo({...initialState, tipo: tipoId}); setShowModal(true); }}>
                            <Plus size={18} />
                        </button>
                    </div>
                    
                    <div className="column-body" style={{ minHeight: '150px' }}>
                        {grupos.map((grupo, index) => (
                            grupo.tipo === tipoId && (
                                <Draggable key={grupo.id.toString()} draggableId={grupo.id.toString()} index={index}>
                                    {(provided) => (
                                        <div 
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            className="flow-card-group" 
                                            style={{ 
                                                ...provided.draggableProps.style,
                                                background: '#fff', 
                                                padding: '15px', 
                                                borderRadius: '12px', 
                                                marginBottom: '10px', 
                                                border: '1px solid #e2e8f0', 
                                                boxShadow: '0 2px 4px rgba(0,0,0,0.02)' 
                                            }}
                                        >
                                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                                <div style={{ display: 'flex', gap: '10px' }}>
                                                    <div {...provided.dragHandleProps} style={{ cursor: 'grab', color: '#cbd5e1' }}>
                                                        <GripVertical size={20} />
                                                    </div>
                                                    <div onClick={() => abrirEdicao(grupo)} style={{ cursor: 'pointer' }}>
                                                        <strong style={{ fontSize: '15px', color: '#1e293b', display: 'block' }}>{grupo.nome}</strong>
                                                        <span style={{ fontSize: '10px', fontWeight: 'bold', color: cor, textTransform: 'uppercase' }}>
                                                            {grupo.metodo_preco === 'substitui' ? 'Fixo' : grupo.metodo_preco === 'maior_valor' ? 'Pizza' : 'Soma'}
                                                        </span>
                                                    </div>
                                                </div>
                                                <span className="badge-regra">{grupo.minimo}-{grupo.maximo} ITENS</span>
                                            </div>
                                        </div>
                                    )}
                                </Draggable>
                            )
                        ))}
                        {provided.placeholder}
                    </div>
                </div>
            )}
        </Droppable>
    );

    return (
        <div className="dashboard-page">
            <Toaster position="top-right" />
            <header className="dashboard-header">
                <div className="header-left-group" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <button onClick={() => navigate('/dashboard')} className="btn-icon-home"><Home size={20} /></button>
                    <div className="divider-vertical"></div>
                    <Link to={`/produtos/${id}`} className="btn-sair" style={{textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px'}}>
                        <ChevronLeft size={18} /> Produtos
                    </Link>
                </div>
                <h2 className="dashboard-logo">Regras de <b>Negócio</b></h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Link to={`/categorias/${id}`} className="btn-secondary" style={{ textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '5px', padding: '8px 15px' }}>
                        <LayoutGrid size={18} /> Categorias
                    </Link>
                    <button onClick={() => setShowModal(true)} className="btn-confirm" style={{ background: '#ea1d2c' }}>+ Criar Grupo</button>
                </div>
            </header>

            <main className="dashboard-container">
                <div className="top-info" style={{ marginBottom: '20px' }}>
                    <h1>Complementos e Sabores</h1>
                    <p style={{ color: '#64748b' }}>Arraste os cards para definir a ordem de exibição no cardápio.</p>
                </div>

                <DragDropContext onDragEnd={onDragEnd}>
                    <div className="flow-columns-container" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '20px' }}>
                        {renderColumn("Obrigatórios", "obrigatorio", <Settings2 size={18}/>, "#ef4444")}
                        {renderColumn("Opcionais", "adicional", <Plus size={18}/>, "#3b82f6")}
                        {renderColumn("Informativos", "informativo", <Info size={18}/>, "#64748b")}
                    </div>
                </DragDropContext>
            </main>

            {showModal && (
                <div className="modal-overlay" onClick={fecharModal}>
                    <div className="modal" style={{ maxWidth: '650px', overflow: 'hidden' }} onClick={e => e.stopPropagation()}>
                        <button type="button" onClick={() => setShowGuia(!showGuia)} style={{float: 'right', background: 'none', border: 'none', color: '#3b82f6', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 'bold'}}>
                            <HelpCircle size={16} /> {showGuia ? 'Fechar Ajuda' : 'Ver Guia de Uso'}
                        </button>
                        <h2 style={{ marginBottom: '20px' }}>Configurar Regras</h2>
                        {showGuia && (
                            <div style={{ background: '#f0f9ff', padding: '15px', borderRadius: '8px', border: '1px solid #bae6fd', marginBottom: '20px' }}>
                                <p style={{ fontSize: '12px', color: '#0c4a6e', margin: 0 }}>
                                    <b>Substituir:</b> Ideal para Marmitex. O preço do item selecionado substitui o valor base.<br/>
                                    <b>Maior Valor:</b> Ideal para Pizzas. Cobra o valor do sabor mais caro selecionado.
                                </p>
                            </div>
                        )}
                        <form onSubmit={handleSalvar}>
                            <label className="label">Nome do Grupo</label>
                            <input className="input" value={novoGrupo.nome} onChange={e => setNovoGrupo({...novoGrupo, nome: e.target.value})} required placeholder="Ex: Escolha o Tamanho..." />
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 0.6fr 0.6fr 1.2fr', gap: '15px', marginTop: '15px' }}>
                                <div>
                                    <label className="label">Tipo</label>
                                    <select className="input" value={novoGrupo.tipo} onChange={e => setNovoGrupo({...novoGrupo, tipo: e.target.value})}>
                                        <option value="obrigatorio">Obrigatório</option>
                                        <option value="adicional">Opcional</option>
                                        <option value="informativo">Informativo</option>
                                    </select>
                                </div>
                                <div><label className="label">Min</label><input type="number" className="input" value={novoGrupo.minimo} onChange={e => setNovoGrupo({...novoGrupo, minimo: e.target.value})} /></div>
                                <div><label className="label">Max</label><input type="number" className="input" value={novoGrupo.maximo} onChange={e => setNovoGrupo({...novoGrupo, maximo: e.target.value})} /></div>
                                <div>
                                    <label className="label">Cálculo</label>
                                    <select className="input" value={novoGrupo.metodo_preco} onChange={e => setNovoGrupo({...novoGrupo, metodo_preco: e.target.value})}>
                                        <option value="soma">Soma (+)</option>
                                        <option value="substitui">Substitui (=)</option>
                                        <option value="maior_valor">Maior Valor</option>
                                    </select>
                                </div>
                            </div>
                            <div style={{ marginTop: '25px' }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                                    <h4 style={{ fontSize: '14px' }}>Itens</h4>
                                    <button type="button" onClick={adicionarItemAoGrupo} className="btn-confirm" style={{ padding: '6px 15px', fontSize: '12px', background: '#3b82f6' }}>+ Item</button>
                                </div>
                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {novoGrupo.itens.map((item, index) => (
                                        <div key={index} style={{ display: 'flex', gap: '10px', marginBottom: '10px' }}>
                                            <input className="input" placeholder="Nome" value={item.nome} onChange={e => atualizarItem(index, 'nome', e.target.value)} style={{ flex: 3 }} required />
                                            <input className="input" placeholder="Preço" value={item.preco} onChange={e => atualizarItem(index, 'preco', e.target.value)} style={{ flex: 1.2 }} />
                                            <button type="button" onClick={() => removerItem(index)} className="btn-delete-small"><Trash2 size={18} /></button>
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <button type="submit" className="btn-confirm" style={{ background: '#ea1d2c', width: '100%', marginTop: '20px' }}>Salvar</button>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}